import { User, WorkspaceUser } from "../../../common/types";
import {
  CollaborationConversationSummary,
  ConversationCollaborator,
} from "../services/collaboration";

export const getUserInitials = (label?: string | null): string => {
  if (!label) return "?";
  const normalized = label.includes("@") ? label.split("@")[0] : label;
  const parts = normalized
    .trim()
    .split(/[\s._-]+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return normalized[0]?.toUpperCase() || "?";
};

export const workspaceMembersForTypeahead = ({
  workspaceUsers,
  currentCollaborators,
  ownerUserId,
  query,
}: {
  workspaceUsers: WorkspaceUser[];
  currentCollaborators: ConversationCollaborator[];
  ownerUserId?: string | null;
  query: string;
}): WorkspaceUser[] => {
  const normalizedQuery = query.trim().toLowerCase();
  const excludedIds = new Set(
    currentCollaborators.map((collaborator) => collaborator.user_id),
  );
  if (ownerUserId) {
    excludedIds.add(ownerUserId);
  }

  return workspaceUsers
    .filter((user) => user.status === "accepted")
    .filter((user) => !excludedIds.has(user.user_id))
    .filter((user) =>
      normalizedQuery.length === 0
        ? true
        : user.email.toLowerCase().includes(normalizedQuery),
    )
    .sort((left, right) => left.email.localeCompare(right.email));
};

export const getConversationOwner = (
  collaborators: ConversationCollaborator[],
): ConversationCollaborator | null =>
  collaborators.find((entry) => entry.role === "owner") || null;

export const buildSharedConversationLabel = ({
  collaborators,
  currentUserId,
}: {
  collaborators: ConversationCollaborator[];
  currentUserId?: string | null;
}): string => {
  const owner = getConversationOwner(collaborators);
  if (!owner) return "Shared chat";

  const otherCollaboratorCount = collaborators.filter(
    (entry) => entry.role !== "owner" && entry.user_id !== currentUserId,
  ).length;

  if (otherCollaboratorCount > 1) {
    return `Shared with ${owner.display_name} + ${otherCollaboratorCount}`;
  }
  return `Shared with ${owner.display_name}`;
};

export interface SpeakerDisplayInfo {
  userId: string;
  label: string;
  isOwner: boolean;
}

// Messages produced by an agent-mode conversation carry a synthetic
// sender id of the form "agent:<conversation id>" rather than a real
// workspace user id. These never match a collaborator, the current
// user, or a workspace user, so without special handling they fall
// through to the raw-id branch and render as literally "agent:2".
const AGENT_SENDER_PREFIX = "agent:";

const parseAgentConversationId = (senderUserId: string): number | null => {
  if (!senderUserId.startsWith(AGENT_SENDER_PREFIX)) return null;
  const rawConversationId = senderUserId.slice(AGENT_SENDER_PREFIX.length);
  const parsed = Number.parseInt(rawConversationId, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getConversationById = (
  conversations: CollaborationConversationSummary[],
  conversationId: string | number | null,
): CollaborationConversationSummary | null => {
  const numericConversationId =
    typeof conversationId === "number"
      ? conversationId
      : typeof conversationId === "string"
        ? Number.parseInt(conversationId, 10)
        : NaN;
  if (!Number.isFinite(numericConversationId)) return null;

  return (
    conversations.find(
      (conversation) => conversation.id === numericConversationId,
    ) || null
  );
};

export const resolveSpeakerDisplayInfo = ({
  senderUserId,
  currentUser,
  collaborators,
  workspaceUsers,
  conversations,
}: {
  senderUserId?: string | null;
  currentUser?: User | null;
  collaborators?: ConversationCollaborator[];
  workspaceUsers?: WorkspaceUser[];
  conversations?: CollaborationConversationSummary[];
}): SpeakerDisplayInfo | null => {
  if (!senderUserId) return null;

  const agentConversationId = parseAgentConversationId(senderUserId);
  if (agentConversationId !== null) {
    const agentConversation = getConversationById(
      conversations || [],
      agentConversationId,
    );
    if (agentConversation) {
      return {
        userId: senderUserId,
        label:
          agentConversation.agentName ||
          agentConversation.title ||
          `Agent ${agentConversation.id}`,
        isOwner: false,
      };
    }
    // The conversation summary may not be loaded yet. Render a readable
    // agent label from the parsed id rather than surfacing the raw
    // "agent:<id>" sender string.
    return {
      userId: senderUserId,
      label: `Agent ${agentConversationId}`,
      isOwner: false,
    };
  }

  const collaboratorMatch = collaborators?.find(
    (entry) => entry.user_id === senderUserId,
  );
  if (collaboratorMatch) {
    return {
      userId: senderUserId,
      label: collaboratorMatch.display_name || collaboratorMatch.email,
      isOwner: collaboratorMatch.role === "owner",
    };
  }

  if (currentUser?.id === senderUserId) {
    return {
      userId: senderUserId,
      label: currentUser.name || currentUser.email,
      isOwner: false,
    };
  }

  const workspaceUser = workspaceUsers?.find(
    (entry) => entry.user_id === senderUserId,
  );
  if (workspaceUser) {
    return {
      userId: senderUserId,
      label: workspaceUser.email,
      isOwner: false,
    };
  }

  return {
    userId: senderUserId,
    label: senderUserId,
    isOwner: false,
  };
};
